/**
 * Fleet overview — what the front desk needs before the first customer walks in.
 *
 * Ordered by urgency rather than by data model: what is wrong, what is
 * happening today, then how the fleet is doing overall.
 */

import React, { useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, CalendarClock, CarFront, Clock, Gauge, ShieldAlert } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { StatTile } from '../ui/stat-tile';
import {
  bookingInterval,
  bookingVehicleId,
  complianceAlerts,
  complianceSummary,
  isLatePickup,
  isOpenEnded,
  isOverdue,
  unassignedBookings,
  utilisation,
  vehicleLabel,
  vehicleStateAt
} from '../../lib/fleet';
import { COMPLIANCE_TONE_STYLES } from './fleetStyles';
import { cn } from '../../lib/utils';

const customerName = (booking) => {
  const c = booking.customer;
  if (typeof c === 'object' && c) return `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return booking.driverName || booking.title || 'Booking';
};

const Row = ({ primary, secondary, trailing, onClick, tone }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-full items-center justify-between gap-3 rounded-md border p-2.5 text-left transition-colors hover:bg-accent',
      tone
    )}
  >
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{primary}</p>
      {secondary && <p className="truncate text-xs text-muted-foreground">{secondary}</p>}
    </div>
    {trailing && <span className="shrink-0 text-xs text-muted-foreground">{trailing}</span>}
  </button>
);

const FleetOverview = ({
  vehicles,
  bookings,
  loading,
  rangeStart,
  rangeEnd,
  now = new Date(),
  priv = { name: (v) => v, rego: (v) => v },
  onSelectBooking,
  onSelectVehicle,
  onCreateBooking,
  onNavigate
}) => {
  const vehicleById = useMemo(() => new Map(vehicles.map((v) => [v.$id, v])), [vehicles]);

  const stats = useMemo(() => {
    const states = vehicles.map((v) => vehicleStateAt(v, bookings, now));
    return {
      total: vehicles.filter((v) => v.status !== 'retired').length,
      out: states.filter((s) => s === 'on_loan').length,
      available: states.filter((s) => s === 'available').length,
      offRoad: states.filter((s) => s === 'off_road' || s === 'maintenance').length
    };
  }, [vehicles, bookings, now]);

  const use = useMemo(
    () => utilisation(vehicles, bookings, rangeStart, rangeEnd, now),
    [vehicles, bookings, rangeStart, rangeEnd, now]
  );

  const overdue = useMemo(() => bookings.filter((b) => isOverdue(b, now)), [bookings, now]);
  const latePickups = useMemo(() => bookings.filter((b) => isLatePickup(b, now)), [bookings, now]);
  const waiting = useMemo(() => unassignedBookings(bookings), [bookings]);
  const alerts = useMemo(() => complianceAlerts(vehicles, now), [vehicles, now]);

  const today = useMemo(() => {
    const out = [];
    const back = [];
    bookings.forEach((b) => {
      if (b.type !== 'loan' || ['cancelled', 'no_show'].includes(b.status)) return;
      const { start, end } = bookingInterval(b, now);
      if (b.status === 'reserved' && start && isSameDay(start, now)) out.push(b);
      if (b.status === 'out' && end && isSameDay(end, now)) back.push(b);
    });
    return { out, back };
  }, [bookings, now]);

  const needsAttention = overdue.length + latePickups.length + waiting.length;

  return (
    <div className="space-y-6">
      {/* Each tile is the link, not a button tucked inside it. */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <StatTile
          icon={CarFront}
          label="Active vehicles"
          value={stats.total}
          hint="Manage the fleet"
          loading={loading}
          onClick={() => onNavigate?.('vehicles')}
        />
        <StatTile
          icon={ArrowUpRight}
          label="Out now"
          value={stats.out}
          hint="With customers or staff"
          tone={stats.out ? 'positive' : 'default'}
          loading={loading}
          onClick={() => onNavigate?.('bookings')}
        />
        <StatTile
          icon={CarFront}
          label="Available"
          value={stats.available}
          hint="Free to book today"
          tone={stats.available ? 'info' : 'warning'}
          loading={loading}
          onClick={() => onNavigate?.('schedule')}
        />
        <StatTile
          icon={AlertTriangle}
          label="Overdue"
          value={overdue.length}
          hint={overdue.length ? 'Not returned on time' : 'Nothing outstanding'}
          tone={overdue.length ? 'critical' : 'default'}
          loading={loading}
          onClick={() => onNavigate?.('bookings')}
        />
        <StatTile
          icon={Gauge}
          label="Utilisation"
          value={`${use.percent}%`}
          hint={`${use.bookedDays} of ${use.capacityDays} vehicle-days`}
          loading={loading}
          onClick={() => onNavigate?.('schedule')}
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Skeleton className="h-64 w-full xl:col-span-2" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Needs attention */}
        <Card className="xl:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4" />
              Needs attention
              {needsAttention > 0 && (
                <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground">
                  {needsAttention}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {needsAttention === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing overdue and nothing waiting on a vehicle.
              </p>
            ) : (
              <>
                {overdue.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                      Not returned ({overdue.length})
                    </p>
                    {overdue.map((booking) => {
                      const vehicle = vehicleById.get(bookingVehicleId(booking));
                      return (
                        <Row
                          key={booking.$id}
                          onClick={() => onSelectBooking?.(booking)}
                          tone="border-red-300 dark:border-red-800"
                          primary={priv.name(customerName(booking))}
                          secondary={`${vehicle ? vehicleLabel(vehicle) : 'Vehicle'} · due ${format(
                            new Date(booking.scheduledEnd),
                            'd MMM'
                          )}`}
                          trailing={booking.driverPhone || ''}
                        />
                      );
                    })}
                  </div>
                )}

                {latePickups.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      Not collected ({latePickups.length})
                    </p>
                    {latePickups.map((booking) => {
                      const vehicle = vehicleById.get(bookingVehicleId(booking));
                      return (
                        <Row
                          key={booking.$id}
                          onClick={() => onSelectBooking?.(booking)}
                          primary={priv.name(customerName(booking))}
                          secondary={`${vehicle ? vehicleLabel(vehicle) : 'Unassigned'} · booked from ${format(
                            new Date(booking.scheduledStart),
                            'd MMM'
                          )}`}
                        />
                      );
                    })}
                  </div>
                )}

                {waiting.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Waiting for a vehicle ({waiting.length})
                    </p>
                    {waiting.map((booking) => (
                      <Row
                        key={booking.$id}
                        onClick={() => onSelectBooking?.(booking)}
                        primary={priv.name(customerName(booking))}
                        secondary={`Needs a car from ${format(new Date(booking.scheduledStart), 'd MMM')}${
                          isOpenEnded(booking) ? '' : ` → ${format(new Date(booking.scheduledEnd), 'd MMM')}`
                        }`}
                        trailing="Assign"
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Today */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4" /> Today
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ArrowUpRight className="h-3.5 w-3.5 text-sky-500" /> Collecting ({today.out.length})
              </p>
              {today.out.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing going out.</p>
              ) : (
                today.out.map((booking) => {
                  const vehicle = vehicleById.get(bookingVehicleId(booking));
                  return (
                    <Row
                      key={booking.$id}
                      onClick={() => onSelectBooking?.(booking)}
                      primary={priv.name(customerName(booking))}
                      secondary={vehicle ? `${vehicleLabel(vehicle)} · ${priv.rego(vehicle.registration)}` : 'No vehicle assigned'}
                    />
                  );
                })
              )}
            </div>

            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" /> Due back ({today.back.length})
              </p>
              {today.back.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing due back.</p>
              ) : (
                today.back.map((booking) => {
                  const vehicle = vehicleById.get(bookingVehicleId(booking));
                  return (
                    <Row
                      key={booking.$id}
                      onClick={() => onSelectBooking?.(booking)}
                      primary={priv.name(customerName(booking))}
                      secondary={vehicle ? `${vehicleLabel(vehicle)} · ${priv.rego(vehicle.registration)}` : ''}
                    />
                  );
                })
              )}
            </div>

            {onCreateBooking && (
              <Button variant="outline" size="sm" className="w-full" onClick={() => onCreateBooking()}>
                <CalendarClock className="mr-1 h-4 w-4" /> New booking
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Compliance */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <ShieldAlert className="h-4 w-4" /> Compliance ({alerts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Every vehicle is current on WoF, rego, RUC and servicing.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {alerts.map((alert) => (
                <button
                  key={`${alert.vehicle.$id}-${alert.key}`}
                  type="button"
                  onClick={() => onSelectVehicle?.(alert.vehicle)}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-md border p-2.5 text-left transition-opacity hover:opacity-80',
                    COMPLIANCE_TONE_STYLES[alert.status]
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{vehicleLabel(alert.vehicle)}</p>
                    <p className="truncate font-mono text-[11px] opacity-80">{priv.rego(alert.vehicle.registration)}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-xs font-semibold">{alert.label}</p>
                    <p className="text-[11px] opacity-80">{complianceSummary(alert)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FleetOverview;
